import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';
import { createPortal } from 'react-dom';
import {
  digitsOnly,
  formatDateBrInput,
  isoToDateBr,
  parseDateBrToIso,
} from '@/shared/lib/utils/dateBr';
import { hojeIsoBr } from '@/shared/lib/utils/dateTimeBr';
import inputStyles from '@/shared/ui/Input/Input.module.css';
import styles from './DateInput.module.css';

export type DateInputProps = {
  label?: string;
  error?: string;
  helperText?: string;
  className?: string;
  id?: string;
  name?: string;
  disabled?: boolean;
  required?: boolean;
  /** Valor ISO `yyyy-mm-dd` (contrato da API). */
  value?: string;
  onChange?: (iso: string) => void;
  onBlur?: () => void;
  /** ISO `yyyy-mm-dd`. Dias anteriores ficam indisponíveis no calendário. */
  minIso?: string;
};

const MESES_PT = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
] as const;

const DIAS_PT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'] as const;

type Segmento = 'dia' | 'mes' | 'ano';
type Partes = { dia: string; mes: string; ano: string };

const SEG_INICIO: Record<Segmento, number> = { dia: 0, mes: 3, ano: 6 };
const SEG_TAMANHO: Record<Segmento, number> = { dia: 2, mes: 2, ano: 4 };
const SEG_ORDEM: Segmento[] = ['dia', 'mes', 'ano'];

function parseIsoParts(iso?: string): { y: number; m: number; d: number } | null {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const [y, m, d] = iso.split('-').map(Number);
  return { y, m, d };
}

function toIso(y: number, m: number, d: number): string {
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function partesDeDisplay(display: string): Partes {
  if (!display) return { dia: '', mes: '', ano: '' };
  const [dia = '', mes = '', ano = ''] = display.split('/');
  return { dia, mes, ano };
}

function partesCompletas(partes: Partes, fallback: Partes): Partes {
  return {
    dia: (partes.dia || fallback.dia).padStart(2, '0').slice(0, 2),
    mes: (partes.mes || fallback.mes).padStart(2, '0').slice(0, 2),
    ano: (partes.ano || fallback.ano).padStart(4, '0').slice(0, 4),
  };
}

function fallbackHoje(): Partes {
  const hoje = parseIsoParts(hojeIsoBr()) ?? { y: 2000, m: 1, d: 1 };
  return {
    dia: String(hoje.d).padStart(2, '0'),
    mes: String(hoje.m).padStart(2, '0'),
    ano: String(hoje.y),
  };
}

function segmentoPorPosicao(posicao: number): Segmento {
  if (posicao <= 2) return 'dia';
  if (posicao <= 5) return 'mes';
  return 'ano';
}

type Celula = {
  iso: string;
  dia: number;
  foraDoMes: boolean;
};

function celulasDoMes(ano: number, mes: number): Celula[] {
  const primeiro = new Date(ano, mes - 1, 1);
  const inicio = primeiro.getDay();
  const diasNoMes = new Date(ano, mes, 0).getDate();
  const diasMesAnterior = new Date(ano, mes - 1, 0).getDate();
  const celulas: Celula[] = [];

  for (let i = inicio - 1; i >= 0; i -= 1) {
    const dia = diasMesAnterior - i;
    const data = new Date(ano, mes - 2, dia);
    celulas.push({
      iso: toIso(data.getFullYear(), data.getMonth() + 1, data.getDate()),
      dia,
      foraDoMes: true,
    });
  }

  for (let dia = 1; dia <= diasNoMes; dia += 1) {
    celulas.push({ iso: toIso(ano, mes, dia), dia, foraDoMes: false });
  }

  let proximo = 1;
  while (celulas.length < 42) {
    const data = new Date(ano, mes, proximo);
    celulas.push({
      iso: toIso(data.getFullYear(), data.getMonth() + 1, data.getDate()),
      dia: proximo,
      foraDoMes: true,
    });
    proximo += 1;
  }

  return celulas;
}

/**
 * Campo de data BR: exibe `dd/mm/aaaa`, persiste ISO no formulario.
 * Digitacao por segmentos (como TimeInput) + calendario em portal (pt-BR).
 */
export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(function DateInput(
  {
    label,
    error,
    helperText,
    className,
    id,
    name,
    disabled,
    required,
    value = '',
    onChange,
    onBlur,
    minIso,
  },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const errorId = error ? `${inputId}-error` : undefined;
  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [display, setDisplay] = useState(() => isoToDateBr(value));
  const lastIso = useRef(value);
  const [open, setOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState<CSSProperties | null>(null);
  const [segmento, setSegmento] = useState<Segmento>('dia');
  const [digitosDoSegmento, setDigitosDoSegmento] = useState('');
  const hoje = hojeIsoBr();
  const selecionado = parseIsoParts(value);
  const inicial = selecionado ?? parseIsoParts(hoje) ?? { y: 2026, m: 1, d: 1 };
  const [viewYear, setViewYear] = useState(inicial.y);
  const [viewMonth, setViewMonth] = useState(inicial.m);

  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

  useEffect(() => {
    if (value !== lastIso.current) {
      lastIso.current = value;
      setDisplay(isoToDateBr(value));
      setDigitosDoSegmento('');
    }
  }, [value]);

  useEffect(() => {
    if (!open) return undefined;
    const partes = parseIsoParts(value) ?? parseIsoParts(hoje);
    if (partes) {
      setViewYear(partes.y);
      setViewMonth(partes.m);
    }
    const fecharAoClicarFora = (event: globalThis.MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', fecharAoClicarFora);
    return () => document.removeEventListener('mousedown', fecharAoClicarFora);
  }, [open, value, hoje]);

  useLayoutEffect(() => {
    if (!open) {
      setPanelStyle(null);
      return;
    }
    const atualizar = () => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const rect = wrap.getBoundingClientRect();
      const margem = 8;
      const largura = Math.max(288, Math.min(320, rect.width));
      const alturaEstimada = 340;
      const espacoAbaixo = window.innerHeight - rect.bottom - margem;
      const espacoAcima = rect.top - margem;
      const abrirAcima = espacoAbaixo < alturaEstimada && espacoAcima > espacoAbaixo;
      let left = rect.left;
      if (left + largura > window.innerWidth - margem) {
        left = Math.max(margem, window.innerWidth - largura - margem);
      }
      setPanelStyle({
        position: 'fixed',
        left,
        top: abrirAcima ? Math.max(margem, rect.top - alturaEstimada - 4) : rect.bottom + 4,
        width: largura,
        zIndex: 1100,
      });
    };
    atualizar();
    window.addEventListener('resize', atualizar);
    window.addEventListener('scroll', atualizar, true);
    return () => {
      window.removeEventListener('resize', atualizar);
      window.removeEventListener('scroll', atualizar, true);
    };
  }, [open]);

  const anos = useMemo(() => {
    const centro = viewYear;
    return Array.from({ length: 21 }, (_, i) => centro - 10 + i);
  }, [viewYear]);

  const celulas = useMemo(() => celulasDoMes(viewYear, viewMonth), [viewYear, viewMonth]);

  const emitirDisplay = (proximoDisplay: string) => {
    setDisplay(proximoDisplay);
    const iso = parseDateBrToIso(proximoDisplay);
    if (iso) {
      lastIso.current = iso;
      onChange?.(iso);
      return;
    }
    if (!proximoDisplay) {
      lastIso.current = '';
      onChange?.('');
    }
  };

  const commitIso = (iso: string) => {
    if (minIso && iso < minIso) return;
    lastIso.current = iso;
    setDisplay(isoToDateBr(iso));
    setDigitosDoSegmento('');
    onChange?.(iso);
  };

  const commitBlur = () => {
    const bruto = inputRef.current?.value ?? display;
    const formatted = formatDateBrInput(bruto);
    const iso = parseDateBrToIso(formatted);
    if (iso) {
      if (minIso && iso < minIso) {
        setDisplay(lastIso.current ? isoToDateBr(lastIso.current) : '');
        return;
      }
      setDisplay(isoToDateBr(iso));
      lastIso.current = iso;
      onChange?.(iso);
    } else if (!digitsOnly(formatted)) {
      setDisplay('');
      lastIso.current = '';
      onChange?.('');
    } else {
      // incompleta ou invalida: restaura ultimo ISO valido se houver
      setDisplay(isoToDateBr(lastIso.current));
    }
    onBlur?.();
  };

  const selecionarSegmento = (proximo: Segmento, limparBuffer = true) => {
    setSegmento(proximo);
    if (limparBuffer) setDigitosDoSegmento('');
    requestAnimationFrame(() => {
      const input = inputRef.current;
      if (!input) return;
      input.focus();
      if (input.value.length < 10) return;
      const inicio = SEG_INICIO[proximo];
      input.setSelectionRange(inicio, inicio + SEG_TAMANHO[proximo]);
    });
  };

  const atualizarSegmento = (alvo: Segmento, valor: string) => {
    const base = partesCompletas(partesDeDisplay(display), fallbackHoje());
    const proximo: Partes = { ...base, [alvo]: valor };
    emitirDisplay(`${proximo.dia}/${proximo.mes}/${proximo.ano}`);
  };

  const handleFocus = () => {
    selecionarSegmento('dia');
  };

  const handleClick = (event: MouseEvent<HTMLInputElement>) => {
    const posicao = event.currentTarget.selectionStart ?? 0;
    selecionarSegmento(segmentoPorPosicao(posicao));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.defaultPrevented) return;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      const idx = SEG_ORDEM.indexOf(segmento);
      const proximo = event.key === 'ArrowLeft'
        ? SEG_ORDEM[Math.max(0, idx - 1)]
        : SEG_ORDEM[Math.min(SEG_ORDEM.length - 1, idx + 1)];
      selecionarSegmento(proximo);
      return;
    }
    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (event.key === 'Backspace' || event.key === 'Delete') {
      event.preventDefault();
      setDigitosDoSegmento('');
      if (!display) {
        selecionarSegmento('dia');
        return;
      }
      const padrao: Partes = { dia: '01', mes: '01', ano: fallbackHoje().ano };
      const atual = partesCompletas(partesDeDisplay(display), padrao);
      const idx = SEG_ORDEM.indexOf(segmento);

      if (atual[segmento] !== padrao[segmento]) {
        atualizarSegmento(segmento, padrao[segmento]);
        selecionarSegmento(segmento);
        return;
      }
      if (idx > 0) {
        const anterior = SEG_ORDEM[idx - 1];
        const proximo = { ...atual, [anterior]: padrao[anterior] };
        emitirDisplay(`${proximo.dia}/${proximo.mes}/${proximo.ano}`);
        selecionarSegmento(anterior);
        return;
      }
      emitirDisplay('');
      selecionarSegmento('dia');
      return;
    }
    if (!/^\d$/.test(event.key) || event.ctrlKey || event.metaKey || event.altKey) return;

    event.preventDefault();
    const input = inputRef.current;
    const selecaoCompleta = input != null
      && input.value.length > 0
      && input.selectionStart === 0
      && input.selectionEnd === input.value.length;
    const segmentoAtual = selecaoCompleta || !display ? 'dia' : segmento;
    const bufferAtual = selecaoCompleta || !display ? '' : digitosDoSegmento;
    const tamanho = SEG_TAMANHO[segmentoAtual];
    const novoBuffer = `${bufferAtual}${event.key}`.slice(-tamanho);

    if (segmentoAtual === 'ano') {
      if (novoBuffer.length === 4) {
        atualizarSegmento('ano', novoBuffer);
        setDigitosDoSegmento('');
        selecionarSegmento('ano');
        return;
      }
      setDigitosDoSegmento(novoBuffer);
      atualizarSegmento('ano', novoBuffer.padEnd(4, '0'));
      selecionarSegmento('ano', false);
      return;
    }

    const limite = segmentoAtual === 'dia' ? 31 : 12;
    const numero = Number(novoBuffer);
    if (novoBuffer.length === 2 && numero >= 1 && numero <= limite) {
      atualizarSegmento(segmentoAtual, novoBuffer);
      setDigitosDoSegmento('');
      selecionarSegmento(segmentoAtual === 'dia' ? 'mes' : 'ano');
      return;
    }
    setDigitosDoSegmento(event.key);
    atualizarSegmento(segmentoAtual, `0${event.key}`);
    selecionarSegmento(segmentoAtual, false);
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const formatted = formatDateBrInput(event.clipboardData.getData('text'));
    emitirDisplay(formatted.length === 10 ? formatted : formatDateBrInput(digitsOnly(formatted)));
    setDigitosDoSegmento('');
  };

  const irMes = (delta: number) => {
    const base = new Date(viewYear, viewMonth - 1 + delta, 1);
    setViewYear(base.getFullYear());
    setViewMonth(base.getMonth() + 1);
  };

  const calendario = open && panelStyle
    ? createPortal(
        <div
          ref={panelRef}
          className={styles.popover}
          style={panelStyle}
          role="dialog"
          aria-label="Selecionar data"
        >
          <div className={styles.nav}>
            <button
              type="button"
              className={styles.navBtn}
              aria-label="Mês anterior"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => irMes(-1)}
            >
              <ChevronLeft size={18} aria-hidden />
            </button>
            <div className={styles.navSelects}>
              <select
                aria-label="Mês"
                value={viewMonth}
                onChange={(e) => setViewMonth(Number(e.target.value))}
              >
                {MESES_PT.map((nome, index) => (
                  <option key={nome} value={index + 1}>
                    {nome}
                  </option>
                ))}
              </select>
              <select
                aria-label="Ano"
                value={viewYear}
                onChange={(e) => setViewYear(Number(e.target.value))}
              >
                {anos.map((ano) => (
                  <option key={ano} value={ano}>
                    {ano}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              className={styles.navBtn}
              aria-label="Próximo mês"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => irMes(1)}
            >
              <ChevronRight size={18} aria-hidden />
            </button>
          </div>
          <div className={styles.weekdays} aria-hidden>
            {DIAS_PT.map((dia) => (
              <span key={dia} className={styles.weekday}>
                {dia}
              </span>
            ))}
          </div>
          <div className={styles.grid}>
            {celulas.map((celula) => {
              const selecionada = celula.iso === value;
              const ehHoje = celula.iso === hoje;
              const bloqueado = Boolean(minIso && celula.iso < minIso);
              return (
                <button
                  key={celula.iso + String(celula.foraDoMes)}
                  type="button"
                  disabled={bloqueado}
                  className={[
                    styles.day,
                    celula.foraDoMes ? styles.dayOutside : '',
                    selecionada ? styles.daySelected : '',
                    ehHoje ? styles.dayToday : '',
                    bloqueado ? styles.dayDisabled : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    if (bloqueado) return;
                    commitIso(celula.iso);
                    setOpen(false);
                    onBlur?.();
                  }}
                >
                  {celula.dia}
                </button>
              );
            })}
          </div>
          <div className={styles.footer}>
            <button
              type="button"
              className={styles.footerBtn}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                commitIso('');
                setOpen(false);
                onBlur?.();
              }}
            >
              Limpar
            </button>
            <button
              type="button"
              className={styles.footerBtn}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                commitIso(hoje);
                setOpen(false);
                onBlur?.();
              }}
            >
              Hoje
            </button>
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <div className={`${inputStyles.field} ${className ?? ''}`.trim()} ref={rootRef}>
      {label ? (
        <label htmlFor={inputId} className={inputStyles.label}>
          {label}
          {required ? (
            <>
              {' '}
              <span className={inputStyles.required} aria-hidden="true">
                *
              </span>
            </>
          ) : null}
        </label>
      ) : null}
      <div className={styles.inputWrap} ref={wrapRef}>
        <input
          ref={inputRef}
          id={inputId}
          name={name}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          spellCheck={false}
          placeholder="dd/mm/aaaa"
          maxLength={10}
          disabled={disabled}
          className={`${inputStyles.input} ${styles.input} ${error ? inputStyles.inputError : ''}`}
          aria-invalid={Boolean(error)}
          aria-describedby={errorId}
          aria-required={required || undefined}
          value={display}
          onChange={() => {
            /* digitacao controlada via onKeyDown (segmentos) */
          }}
          onFocus={handleFocus}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={() => {
            commitBlur();
            window.setTimeout(() => {
              if (
                !rootRef.current?.contains(document.activeElement)
                && !panelRef.current?.contains(document.activeElement)
              ) {
                setOpen(false);
              }
            }, 0);
          }}
        />
        <button
          type="button"
          className={styles.trigger}
          aria-label="Abrir calendário"
          aria-expanded={open}
          aria-haspopup="dialog"
          disabled={disabled}
          tabIndex={-1}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => setOpen((atual) => !atual)}
        >
          <CalendarIcon size={20} aria-hidden />
        </button>
      </div>
      {calendario}
      {error ? (
        <span id={errorId} className={inputStyles.error} role="alert">
          {error}
        </span>
      ) : helperText ? (
        <span className={inputStyles.helper}>{helperText}</span>
      ) : null}
    </div>
  );
});
